
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";

const Login = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [isPasswordResetMode, setIsPasswordResetMode] = useState(false);
  const [debugInfo, setDebugInfo] = useState<string>('');

  // Check URL for password reset token
  useEffect(() => {
    const checkForResetToken = async () => {
      console.log("Checking for reset token in URL");
      
      // For debugging - track all the values we're checking
      const debugData: string[] = [];
      
      // Get the hash fragment from the URL
      const hashFragment = window.location.hash;
      debugData.push(`Hash fragment: ${hashFragment}`);
      
      // Parse hash fragment into searchParams-like object
      const hashParams = new URLSearchParams(hashFragment.substring(1));
      
      // Get search params
      const type = searchParams.get('type');
      const accessToken = searchParams.get('access_token');
      const refreshToken = searchParams.get('refresh_token');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');
      
      debugData.push(`URL search params:`);
      debugData.push(`- type: ${type || 'none'}`);
      debugData.push(`- access_token: ${accessToken ? 'present' : 'none'}`);
      debugData.push(`- refresh_token: ${refreshToken ? 'present' : 'none'}`);
      
      // Check for errors in URL
      if (error) {
        debugData.push(`ERROR detected in URL: ${error}`);
        debugData.push(`Error description: ${errorDescription}`);
        
        toast({
          title: "Authentication Error",
          description: errorDescription || "An error occurred during authentication",
          variant: "destructive",
        });
        
        // Don't continue with password reset if we have an error
        setDebugInfo(debugData.join('\n'));
        return;
      }
      
      // Check current URL
      debugData.push(`Current URL: ${window.location.href}`);
      debugData.push(`Origin: ${window.location.origin}`);
      debugData.push(`Pathname: ${window.location.pathname}`);
      
      // Check ALL possible ways to detect password reset mode
      const isHashReset = hashFragment && 
        (hashFragment.includes('access_token') || 
         hashFragment.includes('type=recovery'));
      
      const isParamReset = (
        (type === 'recovery' || type === 'signup') && 
        accessToken
      );
      
      debugData.push(`IsHashReset: ${isHashReset}`);
      debugData.push(`IsParamReset: ${isParamReset}`);
      
      // Check both hash fragment and search params for reset tokens
      if (isHashReset || isParamReset) {
        debugData.push("✅ Password reset mode detected!");
        setIsPasswordResetMode(true);
        
        // Get token from either hash or params
        const token = hashParams.get('access_token') || accessToken;
        debugData.push(`Token source: ${hashParams.get('access_token') ? 'hash' : 'params'}`);
        
        // Extract email from the hash or params if possible
        let extractedEmail = '';
        if (hashFragment && hashFragment.includes('email=')) {
          extractedEmail = hashParams.get('email') || '';
          debugData.push(`Email from hash: ${extractedEmail}`);
        } else if (searchParams.get('email')) {
          extractedEmail = searchParams.get('email') || '';
          debugData.push(`Email from params: ${extractedEmail}`);
        }
        
        if (extractedEmail) {
          debugData.push(`Setting email state to: ${extractedEmail}`);
          setEmail(extractedEmail);
        }
        
        // If we have an access token, try to get the session
        if (token) {
          try {
            debugData.push("Attempting to set session from URL token");
            
            // Try to set the session from the token in the URL
            const { data, error } = await supabase.auth.setSession({
              access_token: token,
              refresh_token: refreshToken || '',
            });
            
            if (error) {
              debugData.push(`❌ Error setting session: ${error.message}`);
              toast({
                title: "Session Error",
                description: error.message,
                variant: "destructive",
              });
            } else {
              debugData.push("✅ Session set successfully");
              
              if (data?.user?.email) {
                debugData.push(`Setting email from session: ${data.user.email}`);
                setEmail(data.user.email);
              } else {
                debugData.push("No email found in session data");
              }
            }
          } catch (err) {
            debugData.push(`❌ Exception in session handling: ${err}`);
          }
        }

        toast({
          title: "Password Reset",
          description: "Please enter your new password below.",
        });
      } else {
        debugData.push("No reset token found in URL");
        
        // Check if already logged in
        const { data } = await supabase.auth.getSession();
        if (data.session) {
          debugData.push("User already logged in, redirecting to admin");
          navigate('/admin');
        }
      }
      
      // Update debug info state
      setDebugInfo(debugData.join('\n'));
    };

    checkForResetToken();
  }, [navigate, toast, searchParams]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        throw error;
      }
      
      toast({
        title: "Logged in successfully",
        description: "You are now being redirected to the admin panel",
      });
      
      navigate('/admin');
    } catch (error) {
      console.error('Error signing in:', error);
      toast({
        title: "Login failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleResetPassword = async () => {
    if (!resetEmail || !resetEmail.includes('@')) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    
    try {
      // Get the full absolute URL for the redirect
      // Make sure to specify the exact path for the reset page
      const redirectUrl = `${window.location.origin}/login`;
      console.log("Password reset redirect URL:", redirectUrl);
      
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: redirectUrl,
      });
      
      if (error) {
        throw error;
      }
      
      toast({
        title: "Password reset email sent",
        description: "Please check your email to reset your password. Make sure to check your spam folder.",
      });
      
      setIsResetDialogOpen(false);
      setResetEmail('');
    } catch (error) {
      console.error('Error resetting password:', error);
      toast({
        title: "Password reset failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      toast({
        title: "Invalid password",
        description: "Password must be at least 6 characters long",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const { error } = await supabase.auth.updateUser({ 
        password: newPassword
      });
      
      if (error) {
        throw error;
      }
      
      toast({
        title: "Password updated successfully",
        description: "You can now log in with your new password",
      });
      
      // Clear all URL parameters and hash after successful reset
      window.history.replaceState(null, '', window.location.pathname);
      
      setIsPasswordResetMode(false);
      setNewPassword('');
      
      // Do not redirect automatically - let the user log in with the new password
    } catch (error) {
      console.error('Error setting new password:', error);
      toast({
        title: "Password update failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container flex items-center justify-center min-h-screen py-12">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold">Admin Access</CardTitle>
          <CardDescription>
            {isPasswordResetMode 
              ? "Enter your new password" 
              : "Enter your credentials to sign in to the admin panel"}
          </CardDescription>
        </CardHeader>
        
        {isPasswordResetMode ? (
          <form onSubmit={handleSetNewPassword}>
            <CardContent className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <Input 
                  id="new-password" 
                  type="password" 
                  placeholder="Enter your new password" 
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>
              {/* Add debug info in development mode */}
              {process.env.NODE_ENV !== 'production' && debugInfo && (
                <div className="mt-4 p-2 bg-gray-100 rounded text-xs font-mono overflow-auto max-h-40">
                  <pre>{debugInfo}</pre>
                </div>
              )}
            </CardContent>
            <CardFooter>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? "Updating..." : "Update Password"}
              </Button>
            </CardFooter>
          </form>
        ) : (
          <Tabs defaultValue="login" className="w-full">
            <TabsContent value="login" className="mt-0">
              <form onSubmit={handleSignIn}>
                <CardContent className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input 
                      id="email" 
                      type="email" 
                      placeholder="name@example.com" 
                      required 
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input 
                      id="password" 
                      type="password" 
                      required 
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                  <Button 
                    type="button" 
                    variant="link" 
                    className="px-0 text-sm font-medium text-primary"
                    onClick={() => setIsResetDialogOpen(true)}
                  >
                    Forgot password?
                  </Button>
                </CardContent>
                <CardFooter>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? "Logging in..." : "Login"}
                  </Button>
                </CardFooter>
              </form>
            </TabsContent>
          </Tabs>
        )}
        
        <AlertDialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reset Password</AlertDialogTitle>
              <AlertDialogDescription>
                Enter your email address and we'll send you a link to reset your password.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="reset-email">Email</Label>
                <Input 
                  id="reset-email" 
                  type="email" 
                  placeholder="name@example.com" 
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                />
              </div>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
              <AlertDialogAction disabled={isLoading} onClick={handleResetPassword}>
                {isLoading ? "Sending..." : "Send Reset Link"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Card>
    </div>
  );
};

export default Login;
